import { SiteLayout } from "@/components/layout/SiteLayout";
import { SEO } from "@/components/SEO";
import { Link } from "wouter";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { breadcrumbSchema, faqSchema, personSchema, webPageSchema } from "@/lib/seo";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { NetworkNextStep } from "@/components/NetworkNextStep";
import { withNetworkUtm } from "@/lib/network";
const bookFront = "/img/book-cover-front.webp";
const bookBack = "/img/book-cover-back.webp";

const PATH = "/books/your-mind-is-full-of-shit";
const BOOK_ID = `https://shariqrefai.com${PATH}#book`;
const TITLE =
  "Your Mind Is Full of Sh*t, by Shariq Refai, MD, MBA";
const DESCRIPTION =
  "A board-certified psychiatrist's book on overthinking and anxiety. Your Mind Is Full of Sh*t names the four lies that quietly keep modern adults stuck.";
const OG_IMAGE = "/og-ymifos.jpg";

const faqs = [
  {
    question: "When will Your Mind Is Full of Sh*t be released?",
    answer:
      "The book is scheduled for release in 2026 through shrinkMD Publishing. Pre order availability and exact launch date will be announced on this page and through the email list.",
  },
  {
    question: "Who wrote Your Mind Is Full of Sh*t?",
    answer:
      "The book was written by Shariq Refai, MD, MBA, a board-certified psychiatrist with more than fifteen years of experience in psychiatry and mental health care. Dr. Refai holds dual board certifications, an MBA from Duke, and is the founder of the multistate telepsychiatry company shrinkMD.",
  },
  {
    question: "What is the book about?",
    answer:
      "The book is about the four lies most modern adults absorbed without choosing them, control, productivity, positivity, and happiness, and how each one shapes anxiety, overthinking, and emotional exhaustion in everyday life. It walks you through each lie, shows the science underneath it, and gives you small practices that work in the body the way the body actually works.",
  },
  {
    question: "Is this a self-help book?",
    answer:
      "The book sits in the self-help category, but it's written by a clinician for an audience that's tired of typical self-help. There are no affirmations, no five-step morning routines, and no promises that your life will change if you wake up at four. The framework is psychiatry backed, the language is plain, and the disclaimers are honest.",
  },
  {
    question: "Is the book a substitute for therapy or psychiatric treatment?",
    answer:
      "No. The book is educational. It's not therapy. It's not psychiatric treatment. It's not a diagnosis. Reading the book doesn't create a physician-patient relationship with Dr. Refai. If you need clinical care, please see a licensed clinician.",
  },
  {
    question: "Is this book like Atomic Habits or The Body Keeps the Score?",
    answer:
      "The book draws on similar territory but isn't the same as either. Atomic Habits is about behavior change. The Body Keeps the Score is a foundational text on trauma. Your Mind Is Full of Sh*t is closer in spirit to honest, clinician-written books about the everyday patterns that quietly run modern adult life.",
  },
  {
    question: "What does the book cover regarding anxiety, panic, or depression?",
    answer:
      "The book examines anxiety and overthinking in depth from an educational perspective. Panic is discussed at the framework level, with a deeper exploration of the in-the-moment response in the companion book, The Havoc in Your Head. Depression is touched on but is not the primary focus. The book is general educational content. It is not a clinical intervention and is not a substitute for professional care. For clinical care, please consult a licensed clinician.",
  },
  {
    question: "Is there an audiobook?",
    answer:
      "Audiobook details will be announced alongside the print and ebook launch. The email list is the fastest way to be notified.",
  },
  {
    question: "Should I read the workbook at the same time?",
    answer:
      "The workbook is built as a companion. Many readers will use it alongside the main book. You don't have to. Each book stands on its own, and the workbook can be used independently for pattern observation work.",
  },
  {
    question: "Is this related to shrinkMD or the Unstuck app?",
    answer:
      "Dr. Refai founded shrinkMD and created Unstuck. The book is published through shrinkMD Publishing. The book itself is educational and isn't a service of shrinkMD or the app.",
  },
];

const bookJsonLd = {
  "@context": "https://schema.org",
  "@type": "Book",
  "@id": BOOK_ID,
  name: "Your Mind Is Full of Sh*t",
  alternateName: ["Your Mind Is Full of Shit", "YMIFOS"],
  headline:
    "A psychiatrist's guide to understanding overthinking, anxiety, and the lies in your head",
  abstract:
    "A board-certified psychiatrist's framework on the four lies that shape modern adult anxiety, with practices designed for the nervous system the way it actually works.",
  author: { "@id": "https://shariqrefai.com/#person" },
  creator: { "@id": "https://shariqrefai.com/#person" },
  publisher: {
    "@type": "Organization",
    name: "shrinkMD Publishing LLC",
    url: "https://shariqrefai.com/projects",
    memberOf: { "@id": "https://shrinknetwork.com/#organization" },
  },
  isbn: "979-8-950653-00-1",
  bookFormat: "https://schema.org/Paperback",
  datePublished: "2026",
  inLanguage: "en",
  genre: ["Psychology", "Self-Help", "Mental Health", "Anxiety", "Personal Growth"],
  audience: {
    "@type": "Audience",
    audienceType:
      "Adults experiencing anxiety, overthinking, perfectionism, or high-functioning stress",
  },
  about: [
    { "@type": "Thing", name: "Anxiety" },
    { "@type": "Thing", name: "Overthinking" },
    { "@type": "Thing", name: "Nervous system regulation" },
    { "@type": "Thing", name: "Emotional regulation" },
    { "@type": "Thing", name: "High-functioning anxiety" },
    { "@type": "Thing", name: "Mental health literacy" },
  ],
  image: `https://shariqrefai.com${OG_IMAGE}`,
  url: `https://shariqrefai.com${PATH}`,
  workExample: [
    {
      "@type": "Book",
      bookFormat: "https://schema.org/Paperback",
      isbn: "979-8-950653-00-1",
      datePublished: "2026",
      potentialAction: {
        "@type": "ReadAction",
        target: { "@type": "EntryPoint", urlTemplate: "https://shariqrefai.com/books/your-mind-is-full-of-shit" },
      },
    },
    { "@type": "Book", bookFormat: "https://schema.org/EBook", datePublished: "2026" },
  ],
};

export default function BookYMIFOS() {
  const page = webPageSchema({
    path: PATH,
    title: TITLE,
    description: DESCRIPTION,
    image: OG_IMAGE,
    speakableSelectors: [".quick-answer", ".faq"],
    datePublished: "2026-04-01",
    dateModified: "2026-05-08",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Books", path: "/books" },
      { name: "Your Mind Is Full of Sh*t", path: PATH },
    ],
  });
  const faqLd = faqSchema(faqs);
  return (
    <SiteLayout>
      <SEO
        title={TITLE}
        ogTitle="Your Mind Is Full of Sh*t"
        twitterTitle="Your Mind Is Full of Sh*t"
        description={DESCRIPTION}
        url={PATH}
        image={OG_IMAGE}
        type="book"
        keywords="Your Mind Is Full of Sh*t, Shariq Refai book, overthinking book, anxiety book, psychiatrist book, emotional health, four lies"
        schema={[
          personSchema,
          page,
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Books", path: "/books" },
            { name: "Your Mind Is Full of Sh*t", path: PATH },
          ]),
          bookJsonLd,
          faqLd,
        ]}
      />

      {/* Hero */}
      <section className="hero-book pt-24 pb-16 md:pt-32 md:pb-20 px-4 md:px-8 bg-muted/30 border-b border-border/50">
        <div className="container mx-auto max-w-6xl">
          <Breadcrumbs
            items={[
              { name: "Home", href: "/" },
              { name: "Books", href: "/books" },
              { name: "Your Mind Is Full of Sh*t" },
            ]}
          />
        </div>
        <div className="container mx-auto max-w-6xl grid lg:grid-cols-[1.3fr_1fr] gap-12 lg:gap-16 items-start">
          <div className="flex flex-col gap-6">
            {/* Credibility eyebrow (Master Brief hero anatomy) */}
            <p className="text-xs md:text-sm font-bold uppercase tracking-[0.25em] text-accent">
              Book · Shariq Refai, MD, MBA · Board-Certified Psychiatrist
            </p>
            <span className="status-pill self-start text-xs font-bold tracking-widest uppercase text-accent border border-accent/40 px-3 py-1.5 rounded-sm">
              Upcoming Release · 2026
            </span>
            <h1 className="text-3xl md:text-5xl font-serif text-primary leading-[1.1] tracking-tight">
              Your Mind Is Full of Sh*t
            </h1>
            <p className="lede text-xl md:text-2xl font-serif italic text-secondary leading-snug">
              A book for the person who has already tried the apps, read the bestsellers, done the worksheets, and still can't quiet the noise in their own head. Written by a board-certified psychiatrist with more than fifteen years of experience in psychiatry and mental health care.
            </p>
            <dl className="pub-details text-sm text-secondary font-light grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 mt-2">
              <dt className="font-medium text-primary">Publisher</dt>
              <dd>shrinkMD Publishing LLC</dd>
              <dt className="font-medium text-primary">Format</dt>
              <dd>Paperback (Print)</dd>
              <dt className="font-medium text-primary">ISBN-13</dt>
              <dd>979-8-950653-00-1</dd>
            </dl>
            <div className="cta-row flex flex-wrap gap-4 mt-2">
              <a
                href="#notify"
                className="btn-primary inline-flex items-center justify-center bg-primary text-primary-foreground hover:bg-primary/90 transition-colors px-6 py-3 text-sm font-medium tracking-wide rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                Notify Me at Launch
              </a>
              <a
                href="#read-excerpt"
                className="btn-secondary inline-flex items-center justify-center border border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-colors px-6 py-3 text-sm font-medium tracking-wide rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                Read an Excerpt
              </a>
            </div>
          </div>
          <div className="flex flex-col gap-6">
            <img
              src={bookFront}
              alt="Cover of Your Mind Is Full of Sh*t by Shariq Refai, MD, MBA"
              className="w-full max-w-sm mx-auto shadow-2xl" width={500} height={781} loading="lazy" decoding="async"/>
            <img
              src={bookBack}
              alt="Back cover of Your Mind Is Full of Sh*t by Shariq Refai, MD, MBA"
              className="w-full max-w-sm mx-auto shadow-xl opacity-90" width={500} height={781} loading="lazy" decoding="async"/>
          </div>
        </div>
      </section>

      {/* Quick Answer */}
      <section className="quick-answer py-12 md:py-16 px-4 md:px-8 bg-background">
        <div className="container mx-auto max-w-3xl">
          <h2 className="text-2xl md:text-3xl font-serif text-primary mb-4">Quick Answer</h2>
          <p className="text-lg text-secondary font-light leading-relaxed">
            <em>Your Mind Is Full of Sh*t</em> is a 2026 book by Shariq Refai, MD, MBA, a board-certified psychiatrist. The book takes the four lies most modern adults absorbed without choosing them, control, productivity, positivity, and happiness, and shows how each one shapes anxiety, overthinking, and emotional exhaustion. It's published by shrinkMD Publishing. The book is for high-functioning adults who've already tried the popular advice and need something that actually holds up in real life.
          </p>
        </div>
      </section>

      {/* Body sections */}
      <section className="py-12 md:py-16 px-4 md:px-8 bg-muted/20 border-y border-border/40">
        <div className="container mx-auto max-w-3xl prose prose-lg prose-headings:font-serif prose-headings:font-normal prose-headings:text-primary prose-p:font-light prose-p:text-secondary prose-strong:text-primary max-w-none">
          <h2>Why This Book Exists</h2>
          <p>I never planned to write a book like this. I was skeptical of self-help for a long time. Too many books promised quick fixes that didn't last. Too many told people to think positive or manifest a dream, as if a hard life would bow to a cute quote on a coffee mug. Too many left readers feeling worse after the advice fell apart in their hands.</p>
          <p>Then I started watching.</p>
          <p>I watched college students drown in anxiety because they believed they had to control every outcome. I watched parents carry shame because they thought their children needed perfection. I watched athletes fold, not under competition, but under the slow grind of comparison. I watched executives lose a job and feel like their worth walked out the door with it.</p>
          <p>I also watched people come back to life. Not by becoming perfect. Not by hacking happiness. They came back by living in a way that finally felt real.</p>
          <p>This book comes from those rooms and from my own. I'm a board-certified psychiatrist with a Duke MBA, and I've sat with thousands of people. One truth has stayed louder than any diagnosis I've ever made. People can change with the right support, the right context, and care that fits the person in front of you.</p>
          <p>This is the book I wish I could hand every patient before their first appointment.</p>

          <h2>The Big Idea</h2>
          <p>Most modern adult suffering doesn't come from a single dramatic injury. It comes from four quiet lies we absorbed before we knew we were learning them.</p>
          <p>The lie of <strong>control</strong>. The belief that if you can just hold every outcome still, you'll finally feel safe. Control feels like a tool. It acts like a drug.</p>
          <p>The lie of <strong>productivity</strong>. The belief that your worth is determined by your output. Cross enough off the list and you'll finally be allowed to rest. You won't.</p>
          <p>The lie of <strong>positivity</strong>. The belief that good vibes are a duty and that any honest emotion is something to push past. Forced positivity isn't a cure for discomfort. It's a costume, and the costume makes the actual work harder.</p>
          <p>The lie of <strong>happiness</strong>. The belief that happiness is a permanent state you reach if you optimize hard enough. Happiness isn't a destination. It's one feeling inside a much larger range, and chasing it as a constant is what keeps people miserable.</p>
          <p>Once you see these four lies clearly, the patterns they create in the body, the relationships, and the mind start to make sense. The book is built to walk you through each one, show you the wiring underneath, and give you small repeatable practices that meet your nervous system where it actually is.</p>
        </div>
      </section>

      {/* Excerpt */}
      <section id="read-excerpt" className="py-12 md:py-16 px-4 md:px-8 bg-background">
        <div className="container mx-auto max-w-3xl">
          <h2 className="text-2xl md:text-3xl font-serif text-primary mb-6">An Excerpt</h2>
          <blockquote className="border-l-4 border-accent pl-6 md:pl-8 italic text-lg md:text-xl text-secondary font-light leading-relaxed space-y-4">
            <p>"Your mind throws out headlines all day. Some help you. Plenty are noise. When I say your mind is full of sh*t, I don't mean you're defective. I mean the mind is a narrator, not a judge. It guesses. It loves worst-case plots. It gets loudest when you care most. You don't have to salute every line it writes.</p>
            <p>Carry one question with you as you read. Does this thought deserve my breath right now?</p>
            <p>Ask it in the morning, at lunch, and before bed. You'll catch the narrator in the act. You'll notice how often it speaks with full confidence and almost no evidence."</p>
            <cite className="not-italic text-sm text-muted-foreground tracking-wide block mt-4">From the Prologue</cite>
          </blockquote>
        </div>
      </section>

      {/* What You'll Learn */}
      <section className="py-12 md:py-16 px-4 md:px-8 bg-muted/20 border-y border-border/40">
        <div className="container mx-auto max-w-3xl">
          <h2 className="text-2xl md:text-3xl font-serif text-primary mb-4">What You'll Learn</h2>
          <p className="text-lg text-secondary font-light leading-relaxed mb-6">
            This isn't a book that hands you affirmations. It's a working framework that takes your nervous system seriously. By the end, you'll understand:
          </p>
          <ul className="space-y-3 text-secondary font-light leading-relaxed list-disc pl-6">
            <li>Why your brain isn't trying to make you happy and what it's actually doing instead.</li>
            <li>The difference between a thought and thinking, and why that small distinction is one of the most important things you can learn about your own mind.</li>
            <li>How chronic vigilance gets built into the body and what to do about it.</li>
            <li>Why the high performer pattern feels like ambition and runs like dread.</li>
            <li>How to read your nervous system instead of being run by it.</li>
            <li>What "soggy shoes" have to do with chronic stress.</li>
            <li>What's in your invisible backpack and which stones don't belong there.</li>
            <li>Why most positivity advice backfires and what works instead.</li>
            <li>How to interrupt a worry loop without arguing with it.</li>
            <li>What real boundaries feel like in the body, not as a personality trait.</li>
            <li>How to stop outsourcing your worth and inhabit a life that's already yours.</li>
          </ul>
        </div>
      </section>

      {/* Table of Contents */}
      <section className="py-12 md:py-16 px-4 md:px-8 bg-background">
        <div className="container mx-auto max-w-3xl">
          <h2 className="text-2xl md:text-3xl font-serif text-primary mb-4">Table of Contents</h2>
          <p className="text-lg text-secondary font-light leading-relaxed mb-8">
            The book runs four parts and seventeen chapters, plus an introduction, prologue, and conclusion.
          </p>
          <div className="space-y-8">
            {[
              {
                title: "Part I, The Lies That Keep You Stuck",
                items: [
                  "Chapter 1: The Lie of Control",
                  "Chapter 2: The Lie of Productivity",
                  "Chapter 3: The Lie of Positivity",
                  "Chapter 4: The Lie of Happiness",
                ],
              },
              {
                title: "Part II, Your Brain Is Not the Enemy. But It Lies to You.",
                items: [
                  "Chapter 5: The Weather in Your Chest",
                  "Chapter 6: The Weight of Invisible Backpacks",
                  "Chapter 7: The Nervous System's Trap",
                  "Chapter 8: The People Around You Matter More Than You Think",
                ],
              },
              {
                title: "Part III, The Quiet Revolutions That Change Everything",
                items: [
                  "Chapter 9: Stop Solving, Start Accepting",
                  "Chapter 10: Micro Doses of Courage",
                  "Chapter 11: Detach From Outcomes, Attach to Action",
                  "Chapter 12: Reclaiming Choice",
                ],
              },
              {
                title: "Part IV, Living Real, Not Perfect",
                items: [
                  "Chapter 13: Boundaries That Keep You Whole",
                  "Chapter 14: Fail Better, Faster",
                  "Chapter 15: Stop Outsourcing Your Worth",
                  "Chapter 16: The Unapologetic Life",
                  "Chapter 17: shrinkMD Reset: Eight Steps for the Hard Days",
                ],
              },
            ].map((part) => (
              <div key={part.title}>
                <h3 className="text-xl font-serif text-primary mb-3">{part.title}</h3>
                <ul className="space-y-2 text-secondary font-light list-disc pl-6">
                  {part.items.map((it) => (
                    <li key={it}>{it}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <p className="text-secondary font-light mt-8">
            Followed by the Conclusion: <em>The Manifesto of Mental Freedom</em>.
          </p>
        </div>
      </section>

      {/* Who It's For */}
      <section className="py-12 md:py-16 px-4 md:px-8 bg-muted/20 border-y border-border/40">
        <div className="container mx-auto max-w-3xl">
          <h2 className="text-2xl md:text-3xl font-serif text-primary mb-4">Who This Book Is For</h2>
          <p className="text-lg text-secondary font-light leading-relaxed mb-4">
            This book was written for the high-functioning adult who looks fine on paper and feels exhausted in their own head. The executive who can't slow down. The parent who can't sleep. The student who's done all the reading and still can't quiet the noise. The person who's been told they're "an anxious person" and is starting to wonder if that's a permanent identity. The reader who's tired of being told to "just breathe" and watching that advice fall apart in the moment they actually need it.
          </p>
          <p className="text-lg text-secondary font-light leading-relaxed mb-8">
            If you've ever finished a self-help book and felt worse than when you started, you're the right reader for this one.
          </p>
          <h3 className="text-xl font-serif text-primary mb-3">Who this book is not for</h3>
          <p className="text-secondary font-light leading-relaxed">
            This is general educational reading. It is not a clinical text, not therapy, and not individualized medical guidance. If you're in a mental health crisis, this book is not what you need first. If you have severe symptoms, an active eating disorder, suicidal thinking, psychotic symptoms, or a clinical condition that needs urgent care, please reach out to a licensed clinician or call or text 988 in the United States.
          </p>
        </div>
      </section>

      {/* Praise */}
      <section className="py-12 md:py-16 px-4 md:px-8 bg-background">
        <div className="container mx-auto max-w-3xl">
          <h2 className="text-2xl md:text-3xl font-serif text-primary mb-4">Early Praise</h2>
          <p className="text-secondary font-light italic">
            Endorsements coming soon. If you're a reviewer, journalist, or fellow clinician interested in an advance copy, please{" "}
            <Link href="/contact" className="text-accent underline hover:no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-sm">
              contact the team
            </Link>
            .
          </p>
        </div>
      </section>

      {/* Series */}
      <section className="py-12 md:py-16 px-4 md:px-8 bg-muted/20 border-y border-border/40">
        <div className="container mx-auto max-w-3xl">
          <h2 className="text-2xl md:text-3xl font-serif text-primary mb-4">The Series</h2>
          <p className="text-lg text-secondary font-light leading-relaxed">
            <em>Your Mind Is Full of Sh*t</em> is the first book in a three-book set on emotional health and overthinking. The companion{" "}
            <Link href="/books/your-mind-is-full-of-shit-workbook" className="text-accent underline hover:no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-sm">
              workbook
            </Link>{" "}
            turns the framework into hands-on observation work. The follow-up,{" "}
            <Link href="/books/the-havoc-in-your-head" className="text-accent underline hover:no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-sm">
              <em>The Havoc in Your Head</em>
            </Link>
            , gives you the in-the-moment response when the patterns hit. Each book stands on its own. Together they form a working educational library on emotional health.
          </p>
        </div>
      </section>

      {/* About Author */}
      <section className="py-12 md:py-16 px-4 md:px-8 bg-background">
        <div className="container mx-auto max-w-3xl">
          <h2 className="text-2xl md:text-3xl font-serif text-primary mb-4">About the Author</h2>
          <p className="text-lg text-secondary font-light leading-relaxed">
            Shariq Refai, MD, MBA, is a board-certified psychiatrist with more than fifteen years of experience in psychiatry and mental health care. He holds dual board certifications in psychiatry and sports/performance psychiatry, and an MBA from Duke University. He's the founder of{" "}
            <a href={withNetworkUtm("https://shrinkmd.com/", "book_ymifos_page")} target="_blank" rel="noopener noreferrer" className="text-accent underline hover:no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-sm">
              shrinkMD
            </a>
            , a multistate telepsychiatry company, and the creator of the{" "}
            <a href={withNetworkUtm("https://beunstuck.app/", "book_ymifos_page")} target="_blank" rel="noopener noreferrer" className="text-accent underline hover:no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-sm">
              Unstuck
            </a>{" "}
            wellness app. He's a Fellow of the American Psychiatric Association and a member of the Forbes Business Council. He's based in Jacksonville, Florida.{" "}
            <Link href="/about" className="text-accent underline hover:no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-sm">
              Read the full bio.
            </Link>
          </p>
        </div>
      </section>

      {/* Notify CTA */}
      <section id="notify" className="cta-final py-16 md:py-20 px-4 md:px-8 bg-primary text-primary-foreground">
        <div className="container mx-auto max-w-3xl text-center">
          <h2 className="text-2xl md:text-3xl font-serif mb-4">Be the First to Know When the Book Is Available</h2>
          <p className="text-lg font-light leading-relaxed mb-8 opacity-90">
            Pre-orders, launch date, and excerpts will be announced through the email list. No spam. No daily newsletters. One short email when the book is ready.
          </p>
          <Link
            href="/contact"
            className="inline-flex items-center justify-center bg-accent text-white hover:bg-accent/90 transition-colors px-8 py-3 text-sm font-medium tracking-wide rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-primary"
          >
            Request Launch Notification
          </Link>
        </div>
      </section>

      {/* Composite-patient narrative disclosure */}
      <aside
        role="note"
        aria-label="Composite patient narrative disclosure"
        className="composite-disclaimer py-8 px-4 md:px-8 bg-background"
      >
        <div className="container mx-auto max-w-3xl border-l-2 border-accent pl-6 py-4 bg-muted/30">
          <p className="text-sm text-secondary font-light leading-relaxed">
            <strong className="text-primary font-medium">A note on patient examples.</strong>{" "}
            Any patient examples, "I had a patient" framings, or
            clinical-style anecdotes in this book are educational
            composites. They blend and alter details from multiple
            situations and do not describe any identifiable individual or
            any actual patient of Dr. Refai or shrinkMD. They are
            presented for educational illustration only.
          </p>
        </div>
      </aside>

      {/* FAQ */}
      <section className="faq py-12 md:py-16 px-4 md:px-8 bg-background">
        <div className="container mx-auto max-w-3xl">
          <h2 className="text-2xl md:text-3xl font-serif text-primary mb-8">Frequently Asked Questions</h2>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`}>
                <AccordionTrigger className="text-left font-serif text-lg">{f.question}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground font-light leading-relaxed text-base">
                  {f.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-12 px-4 md:px-8 bg-muted/30 border-t border-border/50">
        <div className="container mx-auto max-w-3xl">
          <h3 className="text-lg font-serif text-primary mb-3">Important Educational Disclaimer</h3>
          <p className="text-sm text-secondary font-light leading-relaxed">
            The content on this website is provided for general educational and informational purposes only and is not medical advice or a substitute for individualized psychiatric or medical care.
          </p>
          <p className="text-sm text-secondary font-light leading-relaxed mt-3">
            Viewing this website, reading its content, or submitting information through the website does not establish a physician-patient relationship.
          </p>
          <p className="text-sm text-secondary font-light leading-relaxed mt-3">
            This book is educational and informational. It's not therapy, not psychiatric treatment, not a diagnosis, and not a substitute for individualized care from a licensed clinician.
          </p>
          <ul className="text-sm text-secondary font-light leading-relaxed mt-3 list-disc pl-5 space-y-2">
            <li>The book does not promise specific outcomes, symptom reduction, or clinical results.</li>
            <li>The book is not a substitute for professional mental health care.</li>
            <li>Readers should consult their own licensed clinicians regarding diagnosis or treatment.</li>
            <li>The book is intended for general adult audiences and is not intended for use by minors without appropriate adult guidance.</li>
            <li>If you are in crisis, call or text 988 in the United States, call 911, or go to the nearest emergency room.</li>
          </ul>
        </div>
      </section>

      {/* Your next step in The Shrink Network */}
      <section className="py-16 md:py-20 px-4 md:px-8 bg-background border-t border-border/50">
        <div className="container mx-auto max-w-3xl">
          <NetworkNextStep destinations={["shrinq", "shrinkopedia", "shrinkmd"]} />
        </div>
      </section>
    </SiteLayout>
  );
}
